import { QueryErrorBoundary } from "@components/common/errors";
import { TableSkeleton } from "@components/common/skeletons";
import { useToast } from "@lib/hooks/useToast";
import { useParams } from "@tanstack/react-router";
import { Copy, FileText } from "lucide-react";
import { useTicket } from "../../hooks/useTickets";
import { copyText } from "../../utils";
import { TicketAttachments } from "./TicketAttachments";
import { TicketComments } from "./TicketComments";
import { TicketContextSection } from "./TicketContextSection";
import { TicketHeader } from "./TicketHeader";
import { TicketSolution } from "./TicketSolution";

export function TicketDetail() {
  const { id } = useParams({ from: "/_app/tickets/$id/view" });
  const { data: ticket, isLoading, error, refetch } = useTicket(id);
  const toast = useToast();

  if (isLoading) return <TableSkeleton rows={8} columns={4} />;
  if (error) return <QueryErrorBoundary error={error} refetch={refetch} />;
  if (!ticket) return null;

  return (
    <div className="max-w-[920px] mx-auto space-y-6 animate-fade-in">
      {/* Header — title row + info grid */}
      <TicketHeader ticket={ticket} />

      {/* Context — collapsible */}
      <TicketContextSection context={ticket.context} />

      {/* Description */}
      <section
        className="card bg-base-200 border border-base-300/70 shadow-sm hover:shadow-md hover:border-primary/20 transition-all duration-200"
        aria-labelledby="description-title"
      >
        <div className="card-body space-y-3">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" />
            <h3 id="description-title" className="card-title text-sm font-semibold">
              Description
            </h3>
            <button
              type="button"
              className="btn btn-ghost btn-xs btn-square ml-auto focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:outline-none"
              title="Copier la description"
              onClick={() =>
                copyText(ticket.description).then(() =>
                  toast.success("Description copiée", { dedupeKey: "copy", duration: 1500 }),
                )
              }
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="h-px bg-base-300" />
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{ticket.description}</p>
        </div>
      </section>

      {/* Solution */}
      <TicketSolution ticket={ticket} onUpdate={() => refetch()} />

      {/* Attachments */}
      <TicketAttachments ticketId={ticket.id} propAttachments={ticket.attachments ?? []} />

      {/* Comments */}
      <TicketComments ticketId={ticket.id} comments={ticket.comments} />
    </div>
  );
}
